import type { ConnectionStatus, FreshVibeAPI } from "../types";

export const PROMPT_DISCONNECT_GRACE_MS = 15_000;
export const PROMPT_DISCONNECT_ERROR =
  "Agent connection dropped and the response did not recover. Please retry.";

type TimeoutHandle = ReturnType<typeof setTimeout>;

export function createPromptDisconnectWatchdog(params: {
  api: Pick<FreshVibeAPI, "onConnectionStatus">;
  graceMs?: number;
  onTimeout?: () => void;
  setTimer?: (cb: () => void, ms: number) => TimeoutHandle;
  clearTimer?: (handle: TimeoutHandle) => void;
}) {
  const {
    api,
    graceMs = PROMPT_DISCONNECT_GRACE_MS,
    onTimeout,
    setTimer = (cb, ms) => setTimeout(cb, ms),
    clearTimer = (handle) => clearTimeout(handle),
  } = params;

  let timer: TimeoutHandle | null = null;
  let disposed = false;
  let unsubscribe: (() => void) | undefined;

  const dispose = () => {
    disposed = true;
    if (timer) {
      clearTimer(timer);
      timer = null;
    }
    unsubscribe?.();
    unsubscribe = undefined;
  };

  const promise = new Promise<never>((_, reject) => {
    unsubscribe = api.onConnectionStatus((status: ConnectionStatus) => {
      if (disposed) return;

      // Connection recovered — cancel the pending timeout
      if (status !== "disconnected" && status !== "error") {
        if (timer) {
          clearTimer(timer);
          timer = null;
        }
        return;
      }

      if (timer) return;

      timer = setTimer(() => {
        timer = null;
        if (disposed) return;
        onTimeout?.();
        dispose();
        reject(new Error(PROMPT_DISCONNECT_ERROR));
      }, graceMs);
    }) ?? undefined;
  });

  return { promise, dispose };
}
